import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, MapPin, FileText, Calendar } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { StatusPill, Eyebrow } from "@/components/tac";

export default function Portal() {
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const enter = async () => {
    setBusy(true);
    try {
      const res = await api.post("/portal/access", { token });
      setData(res.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0F17] px-6">
        <div className="card-tac w-full max-w-sm p-8 rise">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo />
          </Link>
          <h1 className="mt-8 font-display text-2xl font-bold text-white">Customer portal</h1>
          <p className="mt-2 text-sm text-slate-400">Enter the access token from your invitation link.</p>
          <div className="relative mt-6">
            <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input data-testid="portal-token-input" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Portal token"
              className="w-full rounded-xl border border-slate-700 bg-[#0d1420] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-amber-500 focus:outline-none" />
          </div>
          <button data-testid="portal-enter-btn" disabled={busy || !token} onClick={enter} className="btn-amber mt-4 w-full rounded-xl py-3 text-sm disabled:opacity-60">
            {busy ? "Verifying…" : "Enter portal"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <header className="glass sticky top-0 z-40 px-5 py-4">
        <div className="mx-auto flex max-w-4xl items-center gap-2.5">
          <Logo />
          <span className="font-display text-lg font-bold text-white">Portal</span>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-10">
        <Eyebrow>Welcome</Eyebrow>
        <h1 className="mt-3 font-display text-3xl font-bold text-white" data-testid="portal-customer-name">{data.customer?.name}</h1>

        <section className="mt-10">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">Your properties</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {data.properties.map((p) => (
              <div key={p.id} className="card-tac p-4" data-testid={`portal-property-${p.id}`}>
                <div className="flex items-center gap-2 text-white"><MapPin className="h-4 w-4 text-cyan-400" /> {p.label}</div>
                <p className="mt-1 text-sm text-slate-400">{p.address}</p>
              </div>
            ))}
            {!data.properties.length && <p className="text-sm text-slate-500">No properties yet.</p>}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">Service history</h2>
          <div className="space-y-3">
            {data.jobs.map((j) => (
              <div key={j.id} className="card-tac flex items-center justify-between p-4" data-testid={`portal-job-${j.id}`}>
                <div>
                  <div className="text-white">{j.title}</div>
                  <div className="mt-1 flex items-center gap-2 font-mono-tac text-xs text-slate-500"><Calendar className="h-3 w-3" /> {(j.scheduled_at || "").slice(0, 10)}</div>
                </div>
                <StatusPill status={j.status} />
              </div>
            ))}
            {!data.jobs.length && <p className="text-sm text-slate-500">No service records yet.</p>}
          </div>
        </section>

        {data.quotes?.length > 0 && (
          <section className="mt-10" data-testid="portal-quotes">
            <h2 className="mb-4 font-display text-lg font-semibold text-white">Estimates & invoices</h2>
            <div className="space-y-3">
              {data.quotes.map((q) => {
                const due = (q.payment_schedule || []).filter((m) => m.status !== "paid").reduce((s, m) => s + (m.amount || 0), 0);
                return (
                  <div key={q.number} className="card-tac flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`portal-quote-${q.number}`}>
                    <div>
                      <div className="flex items-center gap-2 text-white"><FileText className="h-4 w-4 text-amber-400" /> {q.number}</div>
                      <div className="mt-1 font-mono-tac text-xs text-slate-500">
                        Total ${Number(q.total || 0).toLocaleString()} {due > 0 && <span className="text-amber-400">· ${due.toLocaleString()} due</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusPill status={q.status} />
                      <a href={`/q/${q.public_token}`} target="_blank" rel="noreferrer" data-testid={`portal-quote-open-${q.number}`}
                        className="btn-amber inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs">
                        {q.status === "accepted" ? (due > 0 ? "Pay now" : "View") : "Review & approve"}
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">Documents</h2>
          <div className="space-y-3">
            {data.documents.map((d) => (
              <div key={d.id} className="card-tac flex items-center justify-between p-4" data-testid={`portal-doc-${d.id}`}>
                <div className="flex items-center gap-3 text-white"><FileText className="h-4 w-4 text-amber-400" /> {d.summary?.job_title} - Job package</div>
                <span className="font-mono-tac text-xs text-emerald-400">Immutable PDF</span>
              </div>
            ))}
            {!data.documents.length && <p className="text-sm text-slate-500">No documents delivered yet.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
